<script setup lang="ts">
/**
 * Chain-wide item sales widget — "ยอดขายแยกตามรายการ (รวมทุกสาขา)".
 *
 * bead: lucky-war-0ke
 */
import { computed, ref, onMounted, useId, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { ItemSalesOverviewResponse, PromoAttachItem } from '@/api';
import { fetchItemSalesOverview, fetchPromoAttach } from '@/api';
import { ITEM_LABEL_TH, formatBaht, formatQty } from '@/itemSales';
import { itemDisplayName } from '@/lib/itemLabel';
import { apiBrand, lensFromQuery } from '@/lib/brand';

const data = ref<ItemSalesOverviewResponse | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const attachItems = ref<PromoAttachItem[]>([]);
const expanded = ref(false);
const ledgerId = useId();
const route = useRoute();

onMounted(() => load());
watch(() => route.query.brand, load);

async function load() {
  loading.value = true;
  error.value = null;
  attachItems.value = [];
  try {
    const brand = apiBrand(lensFromQuery(route.query.brand));
    data.value = await fetchItemSalesOverview('mtd', undefined, brand);
    // Attach data enriches the top tiles only. POS revenue remains usable when
    // this secondary endpoint is unavailable.
    try {
      attachItems.value = (await fetchPromoAttach('mtd', undefined, brand)).items;
    } catch {
      attachItems.value = [];
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

function fmtDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

// MTD month label — Thai long month + Buddhist era.
function fmtMTDMonth(): string {
  return new Intl.DateTimeFormat('th-TH', { month: 'long', year: 'numeric' }).format(new Date());
}

function fmtThaiDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

function fmtFreshnessStamp(): string {
  const f = data.value?.freshness;
  if (!f) return '';
  const parts: string[] = [];
  if (f.data_through) parts.push(`ข้อมูลถึง ${fmtDate(f.data_through)}`);
  if (f.last_ingest_at) parts.push(`อัปเดตล่าสุด ${fmtThaiDateTime(f.last_ingest_at)}`);
  return parts.join(' · ');
}

function labelFor(item: { item_code: string; name_th: string }): string {
  return itemDisplayName(item);
}

// Top-item KPI tiles: exclude แคบหมู (SPE0053, ฿9/unit) — too low-value
// to surface as a top KPI tile (approved PO scope lucky-war-4qr.11).
const topItemTiles = computed(() =>
  (data.value?.items ?? []).filter((item) => item.item_code !== 'SPE0053').slice(0, 3),
);

const attachByCode = computed(() => new Map(attachItems.value.map((item) => [item.item_code, item])));

function topBranch(itemCode: string) {
  return attachByCode.value.get(itemCode)?.branches[0];
}

function salesFormula(item: ItemSalesOverviewResponse['items'][number]): string {
  const unit = ITEM_LABEL_TH[item.item_code]?.unitLabel ?? 'หน่วย';
  // Adult/kid breakdown (uplift-priced rows only — for POS Base Sales rows
  // the price math would not hold, so they stay quantity-only).
  if (item.kid_units > 0 && item.revenue_source === 'unit_price') {
    return `(ผู้ใหญ่ ${formatQty(item.adult_units)} × ฿${formatQty(item.unit_price)}) + (เด็ก ${formatQty(item.kid_units)} × ฿${formatQty(item.child_price ?? 0)})`;
  }
  if (item.revenue_source === 'unit_price') {
    return `${formatQty(item.total_qty)} ${unit} × ฿${formatQty(item.unit_price)}`;
  }
  return `${formatQty(item.total_qty)} ${unit}`;
}

const adultUnits = computed(() => (data.value?.items ?? [])
  .reduce((sum, item) => sum + (item.adult_units || 0), 0));
const kidUnits = computed(() => (data.value?.items ?? [])
  .reduce((sum, item) => sum + (item.kid_units || 0), 0));
</script>

<template>
  <!-- Loading -->
  <section v-if="loading" class="item-sales-widget state-msg" aria-label="กำลังโหลดข้อมูลรายการรวม">
    <p>กำลังโหลดข้อมูลรายการรวม...</p>
  </section>

  <!-- Error -->
  <section v-else-if="error" class="item-sales-widget state-msg" aria-label="ไม่สามารถโหลดข้อมูลรายการรวมได้">
    <p>ไม่สามารถโหลดข้อมูลรายการรวมได้</p>
    <p style="font-size:0.75rem;color:var(--text-muted)">{{ error }}</p>
    <button class="pager__btn" @click="load()">ลองใหม่</button>
  </section>

  <!-- Empty -->
  <section
    v-else-if="data && data.is_empty"
    class="item-sales-widget item-sales-widget--empty"
    aria-label="ยอดขายแยกตามรายการ (รวมทุกสาขา) — ยังไม่มีข้อมูล"
  >
    <h2 class="item-sales-widget__header">
      <span>ยอดขายแยกตามรายการ (รวมทุกสาขา)</span>
    </h2>
    <p class="item-sales-widget__empty-msg">ยังไม่มีข้อมูลรายการจาก POS</p>
  </section>

  <!-- Data -->
  <section v-else-if="data" class="item-sales-widget" aria-label="ยอดขายแยกตามรายการ (รวมทุกสาขา)">
    <div class="item-sales-widget__summary">
      <p class="item-sales-widget__headline">{{ formatBaht(data.total_item_sales) }}</p>
      <p class="item-sales-widget__context">
        <template v-if="data.is_mtd">{{ fmtMTDMonth() }}</template>
        <template v-else>{{ fmtDate(data.sales_date) }}</template>
        · {{ data.items.length }} รายการ · ข้อมูลจากระบบ POS
        <template v-if="data.is_mtd && fmtFreshnessStamp()"> · {{ fmtFreshnessStamp() }}</template>
      </p>
    </div>

    <!-- Top-items KPI grid -->
    <div v-if="topItemTiles.length >= 2" class="item-sales-widget__kpis">
      <div v-for="it in topItemTiles" :key="it.item_code" class="item-sales-widget__kpi-tile">
        <span class="item-sales-widget__kpi-name">{{ labelFor(it) }}</span>
        <span class="item-sales-widget__kpi-baht">{{ formatBaht(it.computed_sales) }}</span>
        <span class="item-sales-widget__kpi-qty">
          {{ formatQty(it.total_qty) }} {{ ITEM_LABEL_TH[it.item_code]?.unitLabel ?? 'หน่วย' }}
          <template v-if="attachByCode.get(it.item_code)"> · อัตราสั่ง {{ (attachByCode.get(it.item_code)!.chain.attach_rate_today * 100).toFixed(1) }}% ต่อบิล (เดือนนี้)</template>
        </span>
        <span v-if="topBranch(it.item_code)" class="item-sales-widget__kpi-branch">
          สูงสุด: <strong>{{ topBranch(it.item_code)!.name_th }}</strong> {{ (topBranch(it.item_code)!.attach_rate * 100).toFixed(1) }}%
        </span>
      </div>
    </div>

    <div v-if="expanded" :id="ledgerId" class="item-sales-widget__ledger">
      <div v-for="it in data.items" :key="it.item_code" class="item-sales-widget__row">
        <span class="item-sales-widget__row-name">{{ labelFor(it) }}</span>
        <span class="item-sales-widget__row-rule" aria-hidden="true"></span>
        <span class="item-sales-widget__row-formula">{{ salesFormula(it) }}</span>
        <span class="item-sales-widget__row-sales">{{ it.computed_sales > 0 ? formatBaht(it.computed_sales) : '—' }}</span>
      </div>
      <p class="item-sales-widget__ledger-note">
        รายการที่นำเข้าระบบ POS แสดงจำนวนคน · สรุปผู้ใหญ่ {{ formatQty(adultUnits) }} / เด็ก {{ formatQty(kidUnits) }} คน
      </p>
    </div>

    <button
      type="button"
      class="item-sales-widget__toggle"
      :aria-expanded="expanded"
      :aria-controls="ledgerId"
      @click="expanded = !expanded"
    >
      <span>{{ expanded ? 'ย่อรายการเมนู' : `ดูทั้ง ${data.items.length} เมนู` }}</span>
      <span aria-hidden="true">{{ expanded ? '⌃' : '⌄' }}</span>
    </button>

    <p class="item-sales-widget__methodology">
      attach_rate = SUM(item units) / SUM(bills) — MTD (เดือนนี้) · POS ไม่มี bill_id จึงเป็น servings-per-bill (ขอบบนของ attach จริง)
    </p>
  </section>
</template>

<style scoped>
.item-sales-widget {
  margin: 0;
  background: var(--surface);
  border: 0;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

.item-sales-widget--empty {
  opacity: 0.45;
  border-style: dashed;
  border-color: var(--line-light);
  border-top-style: dashed;
  box-shadow: none;
  background: transparent;
}

.item-sales-widget__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-dim);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.5rem 0;
  font-family: 'Kanit', 'Sarabun', sans-serif;
}

.item-sales-widget__summary {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin-bottom: 0.75rem;
}

.item-sales-widget__headline {
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  font-family: 'Kanit', 'Sarabun', sans-serif;
  line-height: 1.2;
}

.item-sales-widget__context {
  font-size: 0.7rem;
  color: var(--text-muted);
  margin: 0;
  font-family: 'Sarabun', sans-serif;
}

.item-sales-widget__empty-msg {
  text-align: center;
  color: var(--text-muted);
  font-size: 0.85rem;
}

/* KPI grid */
.item-sales-widget__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 0.8rem;
}

.item-sales-widget__kpi-tile {
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  padding: 0.7rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.item-sales-widget__kpi-name {
  font-size: 0.7rem;
  color: var(--text);
  font-weight: 700;
  font-family: 'Sarabun', sans-serif;
}

.item-sales-widget__kpi-baht {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--sales);
  font-family: 'Kanit', 'Sarabun', sans-serif;
}

.item-sales-widget__kpi-qty {
  font-size: 0.66rem;
  color: var(--text-muted);
  font-family: 'Sarabun', sans-serif;
}

.item-sales-widget__kpi-branch {
  font-size: 0.64rem;
  color: var(--text-dim);
  font-family: 'Sarabun', sans-serif;
}

.item-sales-widget__kpi-branch strong {
  color: var(--brand-light);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

/* Full menu ledger */
.item-sales-widget__ledger {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--line-light);
  margin-top: 0.15rem;
}

.item-sales-widget__row {
  display: grid;
  grid-template-columns: auto minmax(1.5rem, 1fr) minmax(9rem, auto) minmax(5.25rem, auto);
  align-items: baseline;
  gap: 0.5rem;
  min-height: 2.15rem;
  padding: 0.45rem 0.2rem;
  border-bottom: 1px solid var(--line-light);
}

.item-sales-widget__row-name {
  color: var(--text);
  font: 600 0.72rem/1.35 'Sarabun', sans-serif;
}

.item-sales-widget__row-rule {
  min-width: 1.5rem;
  border-bottom: 1px dotted var(--line);
  transform: translateY(-0.2rem);
}

.item-sales-widget__row-formula {
  color: var(--text-muted);
  font: 0.68rem/1.35 'Sarabun', sans-serif;
  text-align: right;
  white-space: nowrap;
}

.item-sales-widget__row-sales {
  color: var(--text);
  font: 700 0.72rem/1.35 'Kanit', 'Sarabun', sans-serif;
  text-align: right;
  white-space: nowrap;
}

.item-sales-widget__ledger-note {
  margin: 0.5rem 0 0;
  color: var(--text-muted);
  font: 0.63rem/1.45 'Sarabun', sans-serif;
}

.item-sales-widget__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-height: var(--tap-min, 44px);
  width: fit-content;
  margin-top: 0.65rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--brand-soft);
  border-radius: var(--radius-btn);
  background: var(--brand-soft);
  color: var(--brand-light);
  font: 700 0.7rem/1.3 'Kanit', 'Sarabun', sans-serif;
  cursor: pointer;
}

.item-sales-widget__toggle:focus-visible {
  outline: 3px solid var(--brand-light);
  outline-offset: 2px;
}

.item-sales-widget__methodology {
  margin: 0.5rem 0 0;
  font-size: 0.58rem;
  color: var(--text-muted);
  font-family: 'Sarabun', sans-serif;
  line-height: 1.4;
}

.state-msg {
  text-align: center;
  padding: 1rem 0.5rem;
  font-family: 'Sarabun', sans-serif;
  color: var(--text-dim);
  font-size: 0.82rem;
}

@media (max-width: 720px) {
  .item-sales-widget__kpis {
    grid-template-columns: 1fr;
  }

  .item-sales-widget__row {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.15rem 0.65rem;
  }

  .item-sales-widget__row-rule {
    display: none;
  }

  .item-sales-widget__row-formula {
    grid-column: 1;
    grid-row: 2;
    text-align: left;
    white-space: normal;
  }

  .item-sales-widget__row-sales {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-self: center;
  }
}
</style>
